/* tslint:disable:jsx-no-lambda */
import { MUIButton, MUICheckBox, MUIDialog, MUISelect, MUITextField } from '@abb/arcadia-ui-react';
import * as React from 'react';
import { VALIDATION_STATE } from '@module/shared/commonTypes';
import { Option } from '@module/shared/selectOption';
import { IUser, User } from './models';

import './userModal.scss';

type OptionValues = any;

export interface IUserModalState {
  user: IUser;
  countries: Array<Option<OptionValues>>;
  userEmailValidation: VALIDATION_STATE;
  isSaveButtonDisabled: boolean;
  isCreateModal: boolean;
}

export interface IUserModalProps {
  user: IUser;
  isOpen: boolean;
  onClose: () => void;
  onCreate: (user: IUser) => Promise<boolean>;
  onUpdate: (user: IUser) => Promise<boolean>;
  onDelete: () => void;
}

export class UserModal extends React.Component<IUserModalProps, IUserModalState> {
  constructor(props: any) {
    super(props);

    this.state = {
      user: new User(),
      countries: require('./Country.json'),
      userEmailValidation: 'none',
      isSaveButtonDisabled: true,
      isCreateModal: true
    };
  }

  render() {
    const adminOptions = [{ id: 'true', name: 'Admin' }, { id: 'false', name: 'Non-Admin' }];
    const scopeValues = this.state.countries.map((value: any) => ({ id: value.countryID, name: value.countryID }));

    const modalSubmit = this.state.isCreateModal ? 'Create' : 'Save';
    const submitIcon = this.state.isCreateModal ? 'icon_abb_dias icon-abb_add' : 'icon_abb_dias icon-abb_save';

    const formContent = (
      <div className='modal-body row'>
        <div className='col-md-12 modal-content'>

          <div className='row form-group'>
            <MUITextField
              name='email' label='Email' placeholder='Email' size={32}
              value={this.state.user.email}
              isRequired={true}
              errors={this.state.userEmailValidation === 'invalid' ? { email: { message: 'Email cannot exceed 100 characters' } } : undefined}
              onChange={this.handleUserFieldChange} />
          </div>

          <div className='row'>
            <div className='col-lg-12'>
              <MUISelect
                name='adminStatus'
                label='Admin Status'
                size={32}
                dropdownLOV={adminOptions}
                selectedItem={[String(this.state.user.isAdmin)]}
                onChange={this.handleStatusChange}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-md-12'>
              <MUISelect
                name='scopes'
                label='Scopes'
                placeholder='Select Many'
                size={32}
                dropdownLOV={scopeValues}
                selectedItem={this.state.user.scopes}
                multiSelect={true}
                isDisabled={this.state.user.allScopes}
                onChange={this.handleScopeChange}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-12'>
              <MUICheckBox
                label='Scope by all countries'
                checked={this.state.user.allScopes}
                handleToggle={this.handleCheckbox}
              />
            </div>
          </div>

        </div>

        <div className='user-modal-footer'>
          {!this.state.isCreateModal &&
            <MUIButton
              variant='secondary' size={32}
              iconName='icon_abb_dias icon-abb_delete'
              label='Delete'
              onClickButton={this.props.onDelete}
            />
          }
          <MUIButton
            variant='discreet' size={32}
            iconName='icon_abb_dias icon-abb_cancel-1'
            label='Cancel'
            onClickButton={this.handleClose}
          />
          <MUIButton
            variant='primary' size={32}
            iconName={submitIcon}
            label={modalSubmit}
            disabled={this.state.isSaveButtonDisabled}
            onClickButton={this.state.isCreateModal ? this.handleCreate : this.handleUpdate}
          />
        </div>
      </div>
    );

    return (
      <MUIDialog
        variant='default'
        open={this.props.isOpen}
        heading={this.state.isCreateModal ? 'CREATE USER' : 'EDIT USER'}
        content={formContent}
        parentClassName='userModalDialog'
        onClose={this.handleClose}
      />
    );
  }

  componentDidMount() {
    this.setState({ user: this.props.user, isCreateModal: this.props.user.email.length === 0 });
  }

  handleCheckbox = (_e: any, value?: boolean) => {
    this.setState((prevState: any) => {
      const nextUser = { ...prevState.user, allScopes: value ?? !prevState.user.allScopes };
      return { user: nextUser, isSaveButtonDisabled: this.handleSaveButtonDisabled(nextUser, prevState.userEmailValidation) };
    });
  }

  handleSaveButtonDisabled(nextUser: User, userEmailValidation: VALIDATION_STATE) {
    return (nextUser.email === '' || nextUser.email.indexOf(' ') === 0 || userEmailValidation === 'invalid');
  }

  handleStatusChange = (value: any) => {
    const first = Array.isArray(value) ? value[0] : value;
    const id = Array.isArray(value) && typeof first === 'object' && first !== null ? first.id : first;
    this.setState((prevState: any) => {
      const nextUser = { ...prevState.user, isAdmin: String(id) === 'true' };
      return { user: nextUser, isSaveButtonDisabled: this.handleSaveButtonDisabled(nextUser, prevState.userEmailValidation) };
    });
  }

  handleUserFieldChange = (value: string) => {
    const nextUser = { ...this.state.user, email: value };
    if (value.length > 100) {
        this.setState({ user: nextUser, userEmailValidation: 'invalid', isSaveButtonDisabled: true});
    } else if (value.length === 0) {
        this.setState({ user: nextUser, userEmailValidation: 'none', isSaveButtonDisabled: true });
    } else {
        const userEmailValidation: VALIDATION_STATE = 'none';
        this.setState({ user: nextUser, userEmailValidation: 'none', isSaveButtonDisabled: this.handleSaveButtonDisabled(nextUser, userEmailValidation) });
    }
  }

  handleScopeChange = (value: any) => {
    const selectedScopes: string[] = Array.isArray(value)
      ? value.map((v: any) => (typeof v === 'object' && v !== null ? v.id : v))
      : [];
    this.setState((prevState: any) => {
      const updatedUser = { ...prevState.user, scopes: selectedScopes };
      return { user: updatedUser, isSaveButtonDisabled: this.handleSaveButtonDisabled(updatedUser, prevState.userEmailValidation) };
    });
  }

  handleCreate = async () => {
    this.state.user.scopes = this.state.user.allScopes ? [] : this.state.user.scopes;
    const successful = await this.props.onCreate(this.state.user);
    if (successful) {
      this.handleClose();
    }
  }

  handleUpdate = async () => {
    this.state.user.scopes = this.state.user.allScopes ? [] : this.state.user.scopes;
    const successful = await this.props.onUpdate(this.state.user);
    if (successful) {
      this.handleClose();
    }
  }

  handleClose = () => {
    this.setState({ user: new User(), isSaveButtonDisabled: true, userEmailValidation: 'none' });
    this.props.onClose();
  }
}
